import React, { useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { createNewUser, checkINN, getDepartments } from 'service/AdminService'
import { postRegisterUser } from 'store/slices/AdminSlice'
import Notification from 'utils/Notifications'
import { ScaleLoader } from 'react-spinners'
import DepartmentsPanel from './Blocks/DepartmentsPanel'
import styles from './KomurSvyazPage.module.scss'
import {
  getNPAList, createNPA, deleteNPA, updateNPA, downloadNPA,
  getDocumentCategories,
  getNPAFolders, createNPAFolder, updateNPAFolder, deleteNPAFolder,
} from 'service/KomurConnectionService'

// ─────────────────────────────────────────────
// Inline registration form (no nested Layout)
// ─────────────────────────────────────────────
function RegisterTab() {
  const dispatch = useDispatch()
  const [loader, setLoader] = useState(false)
  const [first_name, setFirst_name] = useState('')
  const [last_name, setLast_name] = useState('')
  const [middle_name, setMiddle_name] = useState('')
  const [number_phone, setNumber_phone] = useState('')
  const [inn, setInn] = useState('')
  const [position, setPosition] = useState('')
  const [department_id, setDepartment_id] = useState('')
  const [departments, setDepartments] = useState([])

  const [checkInnVal, setCheckInnVal] = useState('')
  const [innCheckResult, setInnCheckResult] = useState(null)
  const [innCheckLoading, setInnCheckLoading] = useState(false)

  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })

  useEffect(() => {
    getDepartments().then(r => setDepartments(r.data)).catch(() => {})
  }, [])

  const validatePhone = (v) => v.length >= 10 && /^\d+$/.test(v)
  const validateINN = (v) => v && v.toString().length >= 10 && /^\d+$/.test(v)

  const validateField = (name, value) => {
    if (name === 'first_name' || name === 'last_name') {
      return !value || value.trim() === '' ? 'Обязательное поле' : ''
    }
    if (name === 'number_phone') {
      if (!value) return 'Обязательное поле'
      if (!validatePhone(value)) return 'Минимум 10 цифр'
    }
    if (name === 'inn') {
      if (!value) return 'Обязательное поле'
      if (!validateINN(value)) return 'Минимум 10 цифр'
    }
    return ''
  }

  const handleBlur = (name, value) => {
    setTouched(t => ({ ...t, [name]: true }))
    setErrors(e => ({ ...e, [name]: validateField(name, value) }))
  }

  const handleCheckINN = async () => {
    if (!checkInnVal || checkInnVal.length < 10) {
      setNotify({ isOpen: true, message: 'Введите корректный ИНН (минимум 10 цифр)', type: 'warning', sound: 'warning' })
      return
    }
    try {
      setInnCheckLoading(true)
      const response = await checkINN(checkInnVal)
      setInnCheckResult(response.data)
    } catch {
      setNotify({ isOpen: true, message: 'Ошибка при проверке ИНН', type: 'error', sound: 'error' })
    } finally {
      setInnCheckLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const newErrors = {
      first_name: validateField('first_name', first_name),
      last_name: validateField('last_name', last_name),
      number_phone: validateField('number_phone', number_phone),
      inn: validateField('inn', inn),
    }
    setErrors(newErrors)
    setTouched({ first_name: true, last_name: true, number_phone: true, inn: true })
    if (Object.values(newErrors).some(err => err !== '')) {
      setNotify({ isOpen: true, message: 'Пожалуйста, исправьте ошибки в форме', type: 'warning', sound: 'warning' })
      return
    }
    try {
      setLoader(true)
      await createNewUser(first_name, last_name, middle_name, number_phone, inn, inn, true, position, department_id || null)
      dispatch(postRegisterUser({ first_name, last_name, middle_name, number_phone, inn }))
      setNotify({ isOpen: true, message: 'Сотрудник успешно зарегистрирован!', type: 'success', sound: 'success' })
      setFirst_name(''); setLast_name(''); setMiddle_name('')
      setNumber_phone(''); setInn(''); setPosition('')
      setDepartment_id(''); setErrors({}); setTouched({})
    } catch (error) {
      setNotify({ isOpen: true, message: error.response?.data?.message || 'Ошибка регистрации. Попробуйте снова.', type: 'error', sound: 'error' })
    } finally {
      setLoader(false)
    }
  }

  return (
    <div className={styles.regContainer}>
      <div className={styles.regHeader}>
        <p className={styles.regTitle}>Регистрация сотрудника</p>
        <p className={styles.regSubtitle}>Заполните все поля для создания нового сотрудника</p>
      </div>

      {/* INN Check */}
      <div className={styles.innCheckSection}>
        <label className={styles.sectionLabel}>Проверка ИНН в базе данных</label>
        <div className={styles.innCheckWrapper}>
          <input
            type="text"
            className={styles.input}
            style={{ flex: '1 1 260px' }}
            placeholder="Введите ИНН для проверки"
            value={checkInnVal}
            onChange={e => { setCheckInnVal(e.target.value.replace(/\D/g, '')); setInnCheckResult(null) }}
            onKeyPress={e => { if (e.key === 'Enter') { e.preventDefault(); handleCheckINN() } }}
            maxLength={14}
          />
          <button type="button" className={styles.checkButton} onClick={handleCheckINN} disabled={innCheckLoading}>
            {innCheckLoading ? 'Проверка...' : 'Проверить'}
          </button>
          {innCheckResult && (
            <span className={`${styles.innResult} ${innCheckResult.exists ? styles.innExists : styles.innNotExists}`}>
              {innCheckResult.message}
            </span>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Personal */}
        <div className={styles.section}>
          <label className={styles.sectionLabel}>Личные данные</label>
          <div className={styles.formThreeCol}>
            <div className={styles.inputWrapper}>
              <label className={styles.inputLabel}>Фамилия *</label>
              <input type="text" className={`${styles.input} ${touched.last_name && errors.last_name ? styles.inputError : ''}`}
                placeholder="Иванов" value={last_name}
                onChange={e => setLast_name(e.target.value)} onBlur={() => handleBlur('last_name', last_name)} />
              {touched.last_name && errors.last_name && <span className={styles.errorText}>{errors.last_name}</span>}
            </div>
            <div className={styles.inputWrapper}>
              <label className={styles.inputLabel}>Имя *</label>
              <input type="text" className={`${styles.input} ${touched.first_name && errors.first_name ? styles.inputError : ''}`}
                placeholder="Иван" value={first_name}
                onChange={e => setFirst_name(e.target.value)} onBlur={() => handleBlur('first_name', first_name)} />
              {touched.first_name && errors.first_name && <span className={styles.errorText}>{errors.first_name}</span>}
            </div>
            <div className={styles.inputWrapper}>
              <label className={styles.inputLabel}>Отчество</label>
              <input type="text" className={styles.input} placeholder="Иванович"
                value={middle_name} onChange={e => setMiddle_name(e.target.value)} />
            </div>
          </div>
        </div>

        {/* Contact + Position + Department */}
        <div className={styles.section}>
          <label className={styles.sectionLabel}>Контакты и должность</label>
          <div className={styles.formThreeCol}>
            <div className={styles.inputWrapper}>
              <label className={styles.inputLabel}>Номер телефона *</label>
              <input type="tel" className={`${styles.input} ${touched.number_phone && errors.number_phone ? styles.inputError : ''}`}
                placeholder="+996 XXX XXX XXX" value={number_phone}
                onChange={e => setNumber_phone(e.target.value.replace(/\D/g, ''))}
                onBlur={() => handleBlur('number_phone', number_phone)} />
              {touched.number_phone && errors.number_phone && <span className={styles.errorText}>{errors.number_phone}</span>}
            </div>
            <div className={styles.inputWrapper}>
              <label className={styles.inputLabel}>Должность</label>
              <input type="text" className={styles.input} placeholder="Например: Инженер..."
                value={position} onChange={e => setPosition(e.target.value)} />
            </div>
            <div className={styles.inputWrapper}>
              <label className={styles.inputLabel}>Отдел</label>
              <select className={styles.input} value={department_id} onChange={e => setDepartment_id(e.target.value)}>
                <option value="">— Без отдела —</option>
                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* INN */}
        <div className={styles.section}>
          <label className={styles.sectionLabel}>Налоговая информация</label>
          <div className={styles.formTwoCol}>
            <div className={styles.inputWrapper}>
              <label className={styles.inputLabel}>ИНН *</label>
              <input type="text" className={`${styles.input} ${touched.inn && errors.inn ? styles.inputError : ''}`}
                placeholder="Введите ИНН" value={inn}
                onChange={e => setInn(e.target.value.replace(/\D/g, ''))}
                onBlur={() => handleBlur('inn', inn)} maxLength={14} />
              {touched.inn && errors.inn && <span className={styles.errorText}>{errors.inn}</span>}
              <span className={styles.helperText}>ИНН будет использован как пароль</span>
            </div>
          </div>
        </div>

        <div className={styles.submitSection}>
          {loader ? (
            <div className={styles.loaderWrapper}>
              <ScaleLoader color="#736ced" height={35} width={4} />
              <span className={styles.loaderText}>Регистрация...</span>
            </div>
          ) : (
            <button type="submit" className={styles.submitButton}>
              <span className={styles.submitIcon}>✓</span>
              Зарегистрировать сотрудника
            </button>
          )}
        </div>
      </form>
      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

// ─────────────────────────────────────────────
// Helpers for DocsAdminTab
// ─────────────────────────────────────────────
function admFmtDate(str) {
  if (!str) return '—'
  const d = new Date(str)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}
function admFmtSize(bytes) {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} Б`
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} КБ`
  return `${(bytes / 1048576).toFixed(1)} МБ`
}
function admFileIcon(ext) {
  const e = (ext || '').toLowerCase()
  if (e === 'pdf') return '📕'
  if (['doc', 'docx'].includes(e)) return '📝'
  if (['xls', 'xlsx'].includes(e)) return '📊'
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(e)) return '🖼️'
  return '📄'
}

// ─────────────────────────────────────────────
// Docs / НПА Admin Tab
// ─────────────────────────────────────────────

const FOLDER_COLORS_NPA = [
  '#2563eb', '#0891b2', '#059669', '#d97706',
  '#dc2626', '#7c3aed', '#db2777', '#4338ca',
]
const NPA_ICONS = ['📁','📂','📋','📌','🗂️','🔐','📊','📝']

// Модал создания / редактирования папки
function FolderFormModal({ initial = {}, onSave, onClose }) {
  const [name,  setName]  = useState(initial.name  || '')
  const [desc,  setDesc]  = useState(initial.description || '')
  const [icon,  setIcon]  = useState(initial.icon  || '📁')
  const [color, setColor] = useState(initial.color || FOLDER_COLORS_NPA[0])
  const isEdit = Boolean(initial.id)

  return (
    <div className={styles.admModalOverlay} onClick={onClose}>
      <div className={styles.admModal} onClick={e => e.stopPropagation()}>
        <div className={styles.admModalHead}>
          <span style={{ fontSize: 28 }}>{icon}</span>
          <h4 className={styles.admModalTitle}>{isEdit ? 'Редактировать папку' : 'Новая папка НПА'}</h4>
          <button className={styles.admModalClose} onClick={onClose}>✕</button>
        </div>

        <div className={styles.admModalField}>
          <label className={styles.docsUploadLabel}>Название *</label>
          <input className={styles.docsUploadInput} value={name} onChange={e => setName(e.target.value)}
            placeholder="Название папки" autoFocus />
        </div>
        <div className={styles.admModalField}>
          <label className={styles.docsUploadLabel}>Описание <span className={styles.docsOptional}>(необязательно)</span></label>
          <input className={styles.docsUploadInput} value={desc} onChange={e => setDesc(e.target.value)}
            placeholder="Краткое описание…" />
        </div>
        <div className={styles.admModalField}>
          <label className={styles.docsUploadLabel}>Иконка</label>
          <div className={styles.admIconPicker}>
            {NPA_ICONS.map(ic => (
              <button key={ic}
                className={`${styles.admIconBtn} ${ic === icon ? styles.admIconBtnActive : ''}`}
                onClick={() => setIcon(ic)}>{ic}</button>
            ))}
          </div>
        </div>
        <div className={styles.admModalField}>
          <label className={styles.docsUploadLabel}>Цвет</label>
          <div className={styles.admColorPicker}>
            {FOLDER_COLORS_NPA.map(c => (
              <button key={c}
                className={`${styles.admColorBtn} ${c === color ? styles.admColorBtnActive : ''}`}
                style={{ background: c }}
                onClick={() => setColor(c)} />
            ))}
          </div>
        </div>
        <div className={styles.docsUploadActions}>
          <button className={styles.docsConfirmBtn}
            onClick={() => name.trim() && onSave({ name: name.trim(), description: desc.trim(), icon, color })}
            disabled={!name.trim()}>
            {isEdit ? 'Сохранить' : 'Создать'}
          </button>
          <button className={styles.docsCancelBtn} onClick={onClose}>Отмена</button>
        </div>
      </div>
    </div>
  )
}

function DocsAdminTab() {
  const [allDocs,     setAllDocs]     = useState(null)
  const [folders,     setFolders]     = useState(null)
  const [categories,  setCategories]  = useState([])
  const [openFolder,  setOpenFolder]  = useState(null)  // folder object
  const [uploadPending, setUploadPending] = useState(null)
  const [uploading,   setUploading]   = useState(false)
  const [editingId,   setEditingId]   = useState(null)
  const [editTitle,   setEditTitle]   = useState('')
  const [folderModal, setFolderModal] = useState(null)   // null | 'new' | folderObj
  const [notify,      setNotify]      = useState({ isOpen: false, message: '', type: '' })
  const fileInputRef = useRef()

  const reload = () => {
    getNPAList().then(r => setAllDocs(r.data)).catch(() => setAllDocs([]))
    getNPAFolders().then(r => setFolders(r.data)).catch(() => setFolders([]))
  }

  useEffect(() => {
    reload()
    getDocumentCategories().then(r => setCategories(r.data)).catch(() => {})
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Файл выбран ─────────────────────────────────────────────────────
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadPending({
      file,
      title: file.name.replace(/\.[^/.]+$/, ''),
      desc: '',
      categoryId: '',
      folderId: openFolder ? String(openFolder.id) : '',
    })
    e.target.value = ''
  }

  const handleUploadConfirm = async () => {
    if (!uploadPending?.title.trim()) return
    const { file, title, desc, categoryId, folderId } = uploadPending
    const fd = new FormData()
    fd.append('file', file)
    fd.append('title', title.trim())
    if (desc.trim()) fd.append('description', desc.trim())
    if (categoryId)  fd.append('category', categoryId)
    if (folderId)    fd.append('folder', folderId)
    fd.append('is_active', 'true')
    setUploading(true)
    try {
      const r = await createNPA(fd)
      setAllDocs(prev => [r.data, ...(prev || [])])
      if (folderId) {
        setFolders(prev => prev?.map(f =>
          String(f.id) === String(folderId) ? { ...f, doc_count: (f.doc_count || 0) + 1 } : f
        ))
      }
      setUploadPending(null)
      setNotify({ isOpen: true, message: 'НПА успешно загружен', type: 'success' })
    } catch {
      setNotify({ isOpen: true, message: 'Ошибка при загрузке файла', type: 'error' })
    }
    setUploading(false)
  }

  // ── Удаление документа ─────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm('Удалить НПА? Действие необратимо.')) return
    const doc = allDocs?.find(d => d.id === id)
    try {
      await deleteNPA(id)
      setAllDocs(prev => prev.filter(d => d.id !== id))
      if (doc?.folder) {
        setFolders(prev => prev?.map(f =>
          f.id === doc.folder ? { ...f, doc_count: Math.max(0, (f.doc_count || 1) - 1) } : f
        ))
      }
    } catch {
      setNotify({ isOpen: true, message: 'Ошибка при удалении', type: 'error' })
    }
  }

  const handleEditSave = async (id) => {
    if (!editTitle.trim()) return
    try {
      const r = await updateNPA(id, { title: editTitle.trim() })
      setAllDocs(prev => prev.map(d => d.id === id ? { ...d, title: r.data.title } : d))
      setEditingId(null)
    } catch {
      setNotify({ isOpen: true, message: 'Ошибка при сохранении', type: 'error' })
    }
  }

  const handleDownload = (id, fileName) => {
    downloadNPA(id).then(r => {
      const a = document.createElement('a')
      a.href = r.data.file_url
      a.download = fileName || 'document'
      a.click()
    }).catch(() => {})
  }

  // ── Папки CRUD ────────────────────────────────────────────────────
  const handleFolderSave = async (data) => {
    try {
      if (folderModal === 'new') {
        const r = await createNPAFolder(data)
        setFolders(prev => [...(prev || []), { ...r.data, doc_count: 0 }])
        setNotify({ isOpen: true, message: 'Папка создана', type: 'success' })
      } else {
        const r = await updateNPAFolder(folderModal.id, data)
        setFolders(prev => prev?.map(f => f.id === folderModal.id ? { ...f, ...r.data } : f))
        if (openFolder?.id === folderModal.id) setOpenFolder(f => ({ ...f, ...r.data }))
        setNotify({ isOpen: true, message: 'Папка обновлена', type: 'success' })
      }
      setFolderModal(null)
    } catch {
      setNotify({ isOpen: true, message: 'Ошибка при сохранении папки', type: 'error' })
    }
  }

  const handleFolderDelete = async (folder) => {
    if (!window.confirm(`Удалить папку "​${folder.name}​"? Документы останутся без папки.`)) return
    try {
      await deleteNPAFolder(folder.id)
      setFolders(prev => prev?.filter(f => f.id !== folder.id))
      setAllDocs(prev => prev?.map(d =>
        d.folder === folder.id ? { ...d, folder: null, folder_name: '' } : d
      ))
      if (openFolder?.id === folder.id) setOpenFolder(null)
    } catch {
      setNotify({ isOpen: true, message: 'Ошибка при удалении папки', type: 'error' })
    }
  }

  // ── Данные ─────────────────────────────────────────────────────────────
  const folderDocs  = openFolder ? (allDocs?.filter(d => d.folder === openFolder.id) || []) : null
  const looseDocs   = allDocs?.filter(d => !d.folder) || []
  const totalDl     = allDocs?.reduce((s, d) => s + (d.download_count || 0), 0) ?? 0
  const totalViews  = allDocs?.reduce((s, d) => s + (d.view_count    || 0), 0) ?? 0

  // ── Таблица документов ────────────────────────────────────────────────
  const DocTable = ({ docs, emptyText }) => (
    <>
      {!docs && <p className={styles.docsLoading}>Загрузка…</p>}
      {docs && !docs.length && (
        <div className={styles.docsEmpty}>
          <span className={styles.docsEmptyIcon}>📂</span>
          <p className={styles.docsEmptyTitle}>{emptyText || 'Нет документов'}</p>
        </div>
      )}
      {docs && docs.length > 0 && (
        <div className={styles.docsTableWrap}>
          <table className={styles.docsTable}>
            <thead><tr>
              <th>Документ</th>
              <th>Категория</th>
              <th>Папка</th>
              <th>Размер</th>
              <th>Дата</th>
              <th>⬇</th>
              <th>👁</th>
              <th></th>
            </tr></thead>
            <tbody>
              {docs.map(doc => (
                <tr key={doc.id} className={styles.docsRow}>
                  <td className={styles.docsFileCell}>
                    <span className={styles.docsFileIcon}>{admFileIcon(doc.file_extension)}</span>
                    {editingId === doc.id ? (
                      <input
                        className={styles.docsInlineInput}
                        value={editTitle}
                        onChange={e => setEditTitle(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleEditSave(doc.id)
                          if (e.key === 'Escape') setEditingId(null)
                        }}
                        autoFocus
                      />
                    ) : (
                      <span className={styles.docsFileName}>{doc.title}</span>
                    )}
                    {doc.file_extension && (
                      <span className={styles.docsExtBadge}>{doc.file_extension.toUpperCase()}</span>
                    )}
                  </td>
                  <td>
                    {doc.category_name
                      ? <span className={styles.docsCatBadge}>{doc.category_name}</span>
                      : <span className={styles.docsNoCat}>—</span>}
                  </td>
                  <td>
                    {doc.folder_name
                      ? <span className={styles.docsFolderBadge}>📁 {doc.folder_name}</span>
                      : <span className={styles.docsNoCat}>—</span>}
                  </td>
                  <td className={styles.docsMeta}>{admFmtSize(doc.file_size)}</td>
                  <td className={styles.docsMeta}>{admFmtDate(doc.created_at)}</td>
                  <td className={styles.docsCountCell}>{doc.download_count ?? 0}</td>
                  <td className={styles.docsCountCell}>{doc.view_count ?? 0}</td>
                  <td className={styles.docsRowActions}>
                    {editingId === doc.id ? (
                      <>
                        <button className={styles.docsSaveBtn} onClick={() => handleEditSave(doc.id)} title="Сохранить">✓</button>
                        <button className={styles.docsCancelIconBtn} onClick={() => setEditingId(null)} title="Отмена">✕</button>
                      </>
                    ) : (
                      <>
                        <button className={styles.docsEditBtn}
                          onClick={() => { setEditingId(doc.id); setEditTitle(doc.title) }}
                          title="Редактировать">✏️</button>
                        <button className={styles.docsDlBtn}
                          onClick={() => handleDownload(doc.id, doc.file_name)}
                          title="Скачать">⬇</button>
                        <button className={styles.docsDelBtn}
                          onClick={() => handleDelete(doc.id)}
                          title="Удалить">🗑</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )

  // ── Форма загрузки ────────────────────────────────────────────────────
  const UploadForm = () => (
    <div className={styles.docsUploadForm}>
      <div className={styles.docsUploadTop}>
        <span className={styles.docsUploadIcon}>{admFileIcon(uploadPending.file.name.split('.').pop())}</span>
        <span className={styles.docsUploadFilename}>{uploadPending.file.name}</span>
        <span className={styles.docsUploadFilesize}>{admFmtSize(uploadPending.file.size)}</span>
      </div>
      <div className={styles.docsUploadFields}>
        <div className={styles.docsUploadField}>
          <label className={styles.docsUploadLabel}>Название НПА *</label>
          <input className={styles.docsUploadInput}
            value={uploadPending.title}
            onChange={e => setUploadPending(p => ({ ...p, title: e.target.value }))}
            placeholder="Название документа"
            autoFocus />
        </div>
        <div className={styles.docsUploadField}>
          <label className={styles.docsUploadLabel}>Категория</label>
          <select className={styles.docsUploadInput}
            value={uploadPending.categoryId}
            onChange={e => setUploadPending(p => ({ ...p, categoryId: e.target.value }))}>
            <option value="">— Без категории —</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
          </select>
        </div>
        <div className={styles.docsUploadField}>
          <label className={styles.docsUploadLabel}>Папка</label>
          <select className={styles.docsUploadInput}
            value={uploadPending.folderId}
            onChange={e => setUploadPending(p => ({ ...p, folderId: e.target.value }))}>
            <option value="">— Без папки —</option>
            {(folders || []).map(f => <option key={f.id} value={f.id}>{f.icon} {f.name}</option>)}
          </select>
        </div>
        <div className={styles.docsUploadFieldFull}>
          <label className={styles.docsUploadLabel}>Описание <span className={styles.docsOptional}>(необязательно)</span></label>
          <input className={styles.docsUploadInput}
            value={uploadPending.desc}
            onChange={e => setUploadPending(p => ({ ...p, desc: e.target.value }))}
            placeholder="Краткое описание…" />
        </div>
      </div>
      <div className={styles.docsUploadActions}>
        <button className={styles.docsConfirmBtn}
          onClick={handleUploadConfirm}
          disabled={uploading || !uploadPending.title.trim()}>
          {uploading ? '⏳ Загрузка…' : '✓ Загрузить'}
        </button>
        <button className={styles.docsCancelBtn}
          onClick={() => setUploadPending(null)}
          disabled={uploading}>Отмена</button>
      </div>
    </div>
  )

  // ── Рендер: открытая папка ───────────────────────────────────────────
  if (openFolder) {
    return (
      <div className={styles.docsAdmin}>
        <div className={styles.docsAdminHeader}>
          <button className={styles.admBackBtn} onClick={() => setOpenFolder(null)}>‹ Назад</button>
          <div className={styles.admFolderViewTitle}>
            <span style={{ color: openFolder.color, fontSize: 24 }}>{openFolder.icon}</span>
            <div>
              <p className={styles.docsAdminTitle}>{openFolder.name}</p>
              {openFolder.description && <p className={styles.docsAdminSub}>{openFolder.description}</p>}
            </div>
          </div>
          <button className={styles.admEditFolderBtn} onClick={() => setFolderModal(openFolder)}>✏️ Редактировать</button>
          {!uploadPending && (
            <button className={styles.docsUploadBtn} onClick={() => fileInputRef.current?.click()} disabled={uploading}>
              <span>+</span> Загрузить в папку
            </button>
          )}
          <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileSelect} />
        </div>
        {uploadPending && <UploadForm />}
        <DocTable docs={folderDocs} emptyText="Папка пуста" />
        {folderModal && (
          <FolderFormModal
            initial={typeof folderModal === 'object' && folderModal !== 'new' ? folderModal : {}}
            onSave={handleFolderSave}
            onClose={() => setFolderModal(null)}
          />
        )}
        <Notification notify={notify} setNotify={setNotify} />
      </div>
    )
  }

  // ── Рендер: главный вид ───────────────────────────────────────────────
  return (
    <div className={styles.docsAdmin}>

      {/* ── Header ── */}
      <div className={styles.docsAdminHeader}>
        <div className={styles.docsAdminTitleGroup}>
          <p className={styles.docsAdminTitle}>Управление НПА</p>
          <p className={styles.docsAdminSub}>Нормативно-правовые акты — видны всем сотрудникам</p>
        </div>
        <button className={styles.admNewFolderBtn} onClick={() => setFolderModal('new')}>
          📁 Новая папка
        </button>
        {!uploadPending && (
          <button className={styles.docsUploadBtn} onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            <span>+</span> Загрузить НПА
          </button>
        )}
        <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileSelect} />
      </div>

      {/* ── Stats ── */}
      <div className={styles.docsStats}>
        <div className={styles.docsStat}>
          <span className={styles.docsStatNum}>{(folders || []).length}</span>
          <span className={styles.docsStatLabel}>папок</span>
        </div>
        <div className={styles.docsStat}>
          <span className={styles.docsStatNum}>{allDocs?.length ?? '…'}</span>
          <span className={styles.docsStatLabel}>документов</span>
        </div>
        <div className={styles.docsStat}>
          <span className={styles.docsStatNum}>{totalDl}</span>
          <span className={styles.docsStatLabel}>скачиваний</span>
        </div>
        <div className={styles.docsStat}>
          <span className={styles.docsStatNum}>{totalViews}</span>
          <span className={styles.docsStatLabel}>просмотров</span>
        </div>
      </div>

      {/* ── Upload form ── */}
      {uploadPending && <UploadForm />}

      {/* ── Папки ── */}
      {folders !== null && folders.length > 0 && (
        <div className={styles.admFoldersGrid}>
          {folders.map(folder => (
            <div
              key={folder.id}
              className={styles.admFolderCard}
              style={{ '--fc': folder.color || '#2563eb' }}
            >
              <div className={styles.admFolderCardMain}
                onClick={() => setOpenFolder(folder)}
                role="button" tabIndex={0}
                onKeyDown={e => e.key === 'Enter' && setOpenFolder(folder)}>
                <div className={styles.admFolderIcon}>{folder.icon || '📁'}</div>
                <div className={styles.admFolderInfo}>
                  <span className={styles.admFolderName}>{folder.name}</span>
                  {folder.description && <span className={styles.admFolderDesc}>{folder.description}</span>}
                </div>
              </div>
              <div className={styles.admFolderRight}>
                <span className={styles.admFolderCount}>
                  {folder.doc_count}{' '}
                  {folder.doc_count === 1 ? 'документ' : folder.doc_count >= 2 && folder.doc_count <= 4 ? 'документа' : 'документов'}
                </span>
                <button className={styles.admFolderEditBtn}
                  onClick={e => { e.stopPropagation(); setFolderModal(folder) }}
                  title="Редактировать">✏️</button>
                <button className={styles.admFolderDeleteBtn}
                  onClick={e => { e.stopPropagation(); handleFolderDelete(folder) }}
                  title="Удалить">✕</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Документы без папки ── */}
      {folders !== null && folders.length > 0 && (
        <div className={styles.admLooseHeader}>
          <span>📄</span>
          <span className={styles.admLooseTitle}>Без папки</span>
          <span className={styles.admLooseCount}>{looseDocs.length}</span>
        </div>
      )}
      <DocTable
        docs={allDocs === null ? null : looseDocs}
        emptyText="Нет документов— нажмите «Загрузить НПА» чтобы добавить первый документ"
      />

      {/* Модал папки */}
      {folderModal && (
        <FolderFormModal
          initial={folderModal === 'new' ? {} : folderModal}
          onSave={handleFolderSave}
          onClose={() => setFolderModal(null)}
        />
      )}

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

// ─────────────────────────────────────────────
// Main KomurSvyazPage
// ─────────────────────────────────────────────
const TABS = [
  { key: 'departments', icon: '🏢', label: 'Отделы' },
  { key: 'register',    icon: '👤', label: 'Регистрация сотрудников' },
  { key: 'docs',        icon: '📚', label: 'Документы / НПА' },
]

const KomurSvyazPage = () => {
  const [activeTab, setActiveTab] = useState('departments')

  return (
    <div className={styles.page}>
        <div className={styles.tabs}>
          {TABS.map(({ key, icon, label }) => (
            <button
              key={key}
              className={`${styles.tab} ${activeTab === key ? styles.tabActive : ''}`}
              onClick={() => setActiveTab(key)}
            >
              <span className={styles.tabIcon}>{icon}</span>
              {label}
            </button>
          ))}
        </div>

        {activeTab === 'departments' && <DepartmentsPanel />}
        {activeTab === 'register' && <RegisterTab />}
        {activeTab === 'docs' && <DocsAdminTab />}
      </div>
  )
}

export default KomurSvyazPage
